import React, { useEffect, useMemo, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import moment from "moment";
import CustomPagination from "../TablePagination/TablePagination";
import InvoiceBrowseActionsMenu from "./InvoiceBrowseActionsMenu";
import { getInvoicePartyName, isGuestInvoice } from "../../utils/invoice";
import { formatDateTime } from "../../utils/dateTime";

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("en-PK", {
    maximumFractionDigits: 0,
  });

const CustomerInvoicesBrowseTable = ({
  invoices = [],
  loading = false,
  selectedInvoiceId = "",
  onView,
  onEdit,
  onDelete,
  onPrint,
  isReturn = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const rows = useMemo(() => invoices, [invoices]);

  useEffect(() => {
    setPage(0);
  }, [invoices]);

  const paged = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const cellSx = {
    fontFamily: "Quicksand",
    borderBottom: "1px solid #E5E9EB",
    borderRight: "none",
    fontSize: "0.8125rem",
    color: "#1A2426",
    py: 0.85,
    px: 1.25,
  };

  const headSx = {
    ...cellSx,
    fontWeight: 700,
    backgroundColor: "#155f60",
    color: "#fff",
    borderBottom: "none",
    fontSize: "0.7rem",
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    py: 1,
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-dashed border-line bg-subtle/40 px-4 py-10 text-center">
        <p className="text-sm text-muted animate-pulse">Loading invoices…</p>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="rounded-lg border border-dashed border-line bg-subtle/40 px-4 py-10 text-center">
        <p className="text-sm font-semibold text-ink">No invoices found</p>
        <p className="text-xs text-muted mt-1">
          Try another date range or clear search filters.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E9EB",
          overflow: "hidden",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx} align="left">
                Date
              </TableCell>
              <TableCell sx={headSx} align="left">
                Invoice
              </TableCell>
              <TableCell sx={headSx} align="left">
                {isReturn ? "Customer" : "Party"}
              </TableCell>
              <TableCell sx={headSx} align="right">
                Total
              </TableCell>
              <TableCell sx={headSx} align="center" width={56}>
                
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((row) => {
              const selected = row._id === selectedInvoiceId;
              const partyName = getInvoicePartyName(row);
              const guest = isGuestInvoice(row);
              const invoiceLabel =
                row.invoice_no ||
                (row.auto_invoice_no != null
                  ? `A-${row.auto_invoice_no}`
                  : "—");

              return (
                <TableRow
                  key={row._id}
                  hover
                  selected={selected}
                  onClick={() => onView?.(row)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: selected
                      ? "rgba(21, 95, 96, 0.09)"
                      : undefined,
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={cellSx} align="left">
                    <span className="tabular-nums text-inkSecondary">
                      {row.date
                        ? formatDateTime(row.date)
                        : "—"}
                    </span>
                  </TableCell>
                  <TableCell sx={cellSx} align="left">
                    <span className="font-semibold text-ink tabular-nums">
                      {invoiceLabel}
                    </span>
                    {row.invoice_no && row.auto_invoice_no != null && (
                      <span className="block text-[10px] text-muted tabular-nums">
                        Auto {row.auto_invoice_no}
                      </span>
                    )}
                  </TableCell>
                  <TableCell sx={cellSx} align="left">
                    <span className="font-medium text-ink">
                      {partyName || (guest ? "Guest" : "—")}
                    </span>
                    {guest && (
                      <span className="ml-1.5 align-middle text-[10px] font-bold uppercase text-warning bg-warningSoft px-1.5 py-0.5 rounded">
                        Guest
                      </span>
                    )}
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    <span className="font-semibold tabular-nums">
                      {fmtMoney(row.total_amount)}
                    </span>
                  </TableCell>
                  <TableCell
                    sx={cellSx}
                    align="center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <InvoiceBrowseActionsMenu
                      row={row}
                      onView={onView}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onPrint={onPrint}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomPagination
        count={rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(parseInt(value, 10));
          setPage(0);
        }}
      />
    </div>
  );
};

export default CustomerInvoicesBrowseTable;
